import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../config/apiBaseUrl";

type AuthUser = {
  id: string;
  email: string;
  fullName: string;
  country: string;
  currentStorySessionId: string | null;
  isAdmin?: boolean;
  productionMode?: boolean;
  /** ISO timestamp when user accepted upload consent modal */
  imageUploadConsentAt?: string | null;
};

type SignupPayload = {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  country: string;
  acceptedTerms: boolean;
};

export type LoginResult = {
  needsAdminVerification?: boolean;
  isAdmin?: boolean;
};

export type SignupResult = {
  needsEmailVerification?: boolean;
  email?: string;
};

type AuthContextValue = {
  token: string | null;
  user: AuthUser | null;
  isLoading: boolean;
  needsAdminVerification: boolean;
  verificationToken: string | null;
  pendingAdminUser: AuthUser | null;

  getAuthHeader: () => Record<string, string>;
  login: (email: string, password: string) => Promise<LoginResult>;
  signup: (payload: SignupPayload) => Promise<SignupResult>;
  logout: () => void;
  /** Pass `{ silent: true }` to refresh `/me` without toggling global `isLoading` (avoids unmounting wizard routes). */
  refreshUser: (opts?: { silent?: boolean }) => Promise<void>;
  /** Clears server-side upload consent so the user must confirm again (e.g. each visit to the photos step). */
  resetImageUploadConsent: () => Promise<void>;
  acceptImageUploadConsent: () => Promise<void>;
  verifyAdmin: (code: string) => Promise<void>;
  clearAdminVerification: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const TOKEN_STORAGE_KEY = "mysipur_auth_token";
const VERIFICATION_TOKEN_KEY = "mysipur_verification_token";

const toAuthUser = (rawUser: any): AuthUser => {
  const id = rawUser?.id || rawUser?._id;
  const rawConsent = rawUser?.imageUploadConsentAt;
  const imageUploadConsentAt =
    rawConsent != null && rawConsent !== ""
      ? new Date(rawConsent).toISOString()
      : null;

  return {
    id: String(id),
    email: rawUser?.email,
    fullName: rawUser?.fullName,
    country: rawUser?.country,
    currentStorySessionId: rawUser?.currentStorySessionId ?? null,
    isAdmin: rawUser?.isAdmin,
    productionMode: rawUser?.productionMode,
    imageUploadConsentAt,
  };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [needsAdminVerification, setNeedsAdminVerification] = useState<boolean>(false);
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  const [pendingAdminUser, setPendingAdminUser] = useState<AuthUser | null>(null);

  const getAuthHeader = useCallback((): Record<string, string> => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      setIsLoading(true);
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.message || "Login failed");
      }

      const newUser = data?.user;
      if (!newUser) throw new Error("Invalid login response");

      // Admin verification required (production mode, admin email)
      if (data.needsAdminVerification && data.verificationToken) {
        sessionStorage.setItem(VERIFICATION_TOKEN_KEY, data.verificationToken);
        setVerificationToken(data.verificationToken);
        setPendingAdminUser(toAuthUser(newUser));
        setNeedsAdminVerification(true);
        setToken(null);
        setUser(null);
        setIsLoading(false);
        return { needsAdminVerification: true };
      }

      const newToken = data?.token;
      if (!newToken) throw new Error("Invalid login response");

      sessionStorage.setItem(TOKEN_STORAGE_KEY, newToken);
      setToken(newToken);
      setUser(toAuthUser({ ...newUser, isAdmin: data.isAdmin, productionMode: data.productionMode }));
      setNeedsAdminVerification(false);
      setVerificationToken(null);
      setPendingAdminUser(null);
      setIsLoading(false);
      return { isAdmin: data.isAdmin === true };
    },
    []
  );

  const signup = useCallback(
    async (payload: SignupPayload): Promise<SignupResult> => {
      setIsLoading(true);
      const res = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.message || "Signup failed");
      }

      if (data.needsEmailVerification) {
        setIsLoading(false);
        return {
          needsEmailVerification: true,
          email: typeof data.email === "string" ? data.email : undefined,
        };
      }

      const newToken = data?.token;
      const newUser = data?.user;
      if (!newToken || !newUser) throw new Error("Invalid signup response");

      sessionStorage.setItem(TOKEN_STORAGE_KEY, newToken);
      setToken(newToken);
      setUser(toAuthUser({ ...newUser, isAdmin: data.isAdmin, productionMode: data.productionMode }));
      setIsLoading(false);
      return {};
    },
    []
  );

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(VERIFICATION_TOKEN_KEY);
    setToken(null);
    setUser(null);
    setNeedsAdminVerification(false);
    setVerificationToken(null);
    setPendingAdminUser(null);
    setIsLoading(false);
  }, []);

  const clearAdminVerification = useCallback(() => {
    sessionStorage.removeItem(VERIFICATION_TOKEN_KEY);
    setNeedsAdminVerification(false);
    setVerificationToken(null);
    setPendingAdminUser(null);
  }, []);

  const verifyAdmin = useCallback(async (code: string) => {
    const tempToken = sessionStorage.getItem(VERIFICATION_TOKEN_KEY) || verificationToken;
    if (!tempToken) throw new Error("Verification session expired. Please log in again.");

    const res = await fetch(`${API_BASE_URL}/auth/verify-admin`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tempToken, code }),
    });

    const data = await res.json().catch(() => null);
    if (!res.ok) {
      throw new Error(data?.message || "Verification failed");
    }

    const newToken = data?.token;
    const newUser = data?.user;
    if (!newToken || !newUser) throw new Error("Invalid verification response");

    sessionStorage.removeItem(VERIFICATION_TOKEN_KEY);
    sessionStorage.setItem(TOKEN_STORAGE_KEY, newToken);
    setToken(newToken);
    setUser(toAuthUser({ ...newUser, isAdmin: data.isAdmin, productionMode: data.productionMode }));
    setNeedsAdminVerification(false);
    setVerificationToken(null);
    setPendingAdminUser(null);
  }, [verificationToken]);

  const refreshMe = useCallback(
    async (authToken: string) => {
      const res = await fetch(`${API_BASE_URL}/auth/me`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || "Unauthorized");
      }

      return toAuthUser({
        ...data.user,
        isAdmin: data.isAdmin,
        productionMode: data.productionMode,
      });
    },
    []
  );

  const refreshUser = useCallback(async (opts?: { silent?: boolean }) => {
    const authToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (!authToken) {
      setUser(null);
      setToken(null);
      return;
    }
    const silent = opts?.silent === true;
    if (!silent) setIsLoading(true);
    try {
      const u = await refreshMe(authToken);
      setUser(u);
      setToken(authToken);
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [refreshMe]);

  const resetImageUploadConsent = useCallback(async () => {
    const authToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (!authToken) throw new Error("Not signed in");

    const res = await fetch(`${API_BASE_URL}/auth/reset-image-upload-consent`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    const data = await res.json().catch(() => null);
    if (!res.ok || !data?.success) {
      throw new Error(data?.message || "Failed to reset upload consent");
    }

    setUser(
      toAuthUser({
        ...data.user,
        isAdmin: data.isAdmin,
        productionMode: data.productionMode,
      })
    );
  }, []);

  const acceptImageUploadConsent = useCallback(async () => {
    const authToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (!authToken) throw new Error("Not signed in");

    const res = await fetch(`${API_BASE_URL}/auth/accept-image-upload-consent`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    const data = await res.json().catch(() => null);
    if (!res.ok || !data?.success) {
      throw new Error(data?.message || "Failed to save consent");
    }

    setUser(
      toAuthUser({
        ...data.user,
        isAdmin: data.isAdmin,
        productionMode: data.productionMode,
      })
    );
  }, []);

  useEffect(() => {
    const storedToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (!storedToken) {
      setIsLoading(false);
      return;
    }

    setToken(storedToken);
    refreshMe(storedToken)
      .then((u) => setUser(u))
      .catch(() => {
        // If token is invalid/expired, drop it and start clean.
        sessionStorage.removeItem(TOKEN_STORAGE_KEY);
        setToken(null);
        setUser(null);
      })
      .finally(() => setIsLoading(false));
  }, [refreshMe]);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      user,
      isLoading,
      needsAdminVerification,
      verificationToken,
      pendingAdminUser,
      getAuthHeader,
      login,
      signup,
      logout,
      refreshUser,
      resetImageUploadConsent,
      acceptImageUploadConsent,
      verifyAdmin,
      clearAdminVerification,
    }),
    [
      token,
      user,
      isLoading,
      needsAdminVerification,
      verificationToken,
      pendingAdminUser,
      getAuthHeader,
      login,
      signup,
      logout,
      refreshUser,
      resetImageUploadConsent,
      acceptImageUploadConsent,
      verifyAdmin,
      clearAdminVerification,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

